import locationDataRaw from '@/data/locations-data.json';
import type {
  LocationData,
  LocationContext,
  LivingStandard,
  PageType,
  PageTypeConfig,
} from '@/types/locations';
import { UK_COORDINATES } from './uk-coordinates';

export { LOCATION_PAGE_TYPES, TOWN_PAGE_TYPES, CONSOLIDATED_PAGE_TYPES, isIndexedLocationPage, indexedPageTypesFor } from './locationTiers';

const locationContext = locationDataRaw as LocationContext;
const allLocations = locationContext.locations;

export const statePension = locationContext.statePension;
export const swrMultiplier = locationContext.swrMultiplier;
export const regionCost = locationContext.regionCost;
export const regionIndex = locationContext.regionIndex;
export const authorityToRegion = locationContext.authorityToRegion;

/** PLSA Retirement Living Standards, UK-wide. */
export const livingStandards = locationContext.livingStandards;
export const livingStandardsCouple = locationContext.livingStandardsCouple;
export const defaultLivingStandard = locationContext.defaultLivingStandard;

/** UK average annual living costs - the PLSA tier a location's headline figure uses. */
export const ukAverageLivingCosts = livingStandards[defaultLivingStandard];
export const ukAverageHousePrice = locationContext.ukAverageHousePrice;

/** The target at UK-average living costs (£503,800 at the 2026 figures). */
export const ukRetirementNumber = (ukAverageLivingCosts - statePension) * swrMultiplier;

/**
 * "1% below", "7% above" or "in line with" (withPercent), or just
 * "below"/"above"/"in line with". Copy reads "sit {livingCostsVsUk} the UK average".
 */
export function compareToUk(value: number, uk: number, withPercent: boolean): string {
  const pct = Math.round(Math.abs(value / uk - 1) * 100);
  if (pct === 0) return 'in line with';
  const direction = value < uk ? 'below' : 'above';
  return withPercent ? `${pct}% ${direction}` : direction;
}

/** The ONS regional price-level area behind a location's living costs. */
export function priceAreaFor(location: LocationData): string {
  switch (location.region) {
    case 'london': return 'London';
    case 'scotland': return 'Scotland';
    case 'wales': return 'Wales';
    case 'northern-ireland': return 'Northern Ireland';
    default: return 'England outside London';
  }
}

/** When the dataset was last regenerated, for "last updated" copy. */
export const dataGeneratedAt = locationContext.generatedAt;
export const dataSources = locationContext.sources;

/**
 * Annual living costs for a location at a given PLSA standard. The regional index
 * is the ONS regional consumer price level (excludes rent, owner-occupier housing
 * costs and council tax) - housing is handled separately via house price.
 */
export function livingCostsFor(location: LocationData, standard: LivingStandard): number {
  return Math.round(livingStandards[standard] * location.livingCostIndex);
}

/** Pot needed at the 25× rule to cover the gap left after the State Pension. */
export function potNeededForCosts(annualLivingCosts: number, includeStatePension = true): number {
  const gap = includeStatePension ? annualLivingCosts - statePension : annualLivingCosts;
  return gap > 0 ? gap * swrMultiplier : 0;
}

export function getLocationBySlug(slug: string): LocationData | undefined {
  return allLocations.find(loc => loc.slug === slug);
}

export function getAllLocations(): LocationData[] {
  return allLocations.map(loc => ({
    ...loc,
    coordinates: UK_COORDINATES[loc.slug],
  }));
}

export function getPageTypeConfig(pageType: PageType): PageTypeConfig {
  const configs: Record<PageType, PageTypeConfig> = {
    'how-much-do-i-need-to-retire': {
      pageType: 'how-much-do-i-need-to-retire',
      h1Template: 'Retirement in {location}',
      introTemplate: '{location} offers a {comparison} cost of living compared to the UK average. With average house prices at {housePrice} and annual living costs of {livingCosts}, here\'s what you need to know about retiring comfortably in {location}.',
      heroSubtext: 'Work out if you\'re on track - we\'ll help you understand where you stand',
      ctaText: 'See if you\'re on track',
      ctaLink: '/login?ref=location-primary',
      ctaKind: 'signup',
      metaDescription: 'Find out how much you need to retire in {location}, based on local house prices and living costs. Get your personalised retirement number in minutes.',
      faqs: [
        {
          question: 'How much money do I need to retire in {location}?',
          answer: 'It depends on your annual living costs. The standard method: take what you\'ll spend each year in retirement, subtract your State Pension, and multiply the gap by 25. The calculator on this page does this using {location}\'s average living costs, and you can adjust the figures to match your own lifestyle.',
        },
        {
          question: 'What is the 4% rule?',
          answer: 'The 4% rule says you can withdraw 4% of your retirement pot in your first year of retirement, adjusting for inflation each year after, with a low historical risk of running out over 30 years. It\'s the basis of the 25× multiplier: a pot of 25 times your annual spending gap supports a 4% withdrawal rate.',
        },
        {
          question: 'Does the State Pension count towards my retirement number?',
          answer: 'Yes, and it makes a big difference. The full new State Pension is £12,548 per year (2026/27), paid from age 67. You only need your savings and pensions to cover the gap between your annual living costs and that amount, which is why the calculation subtracts it before applying the 25× multiplier.',
        },
        {
          question: 'How much do I need to retire early, before the State Pension starts?',
          answer: 'Retiring before 67 means your pot has to cover your full living costs for the bridge years until the State Pension begins, with nothing subtracted. Each bridge year adds roughly one year of full living costs to your target, so retiring at 60 in {location} needs a meaningfully bigger pot than retiring at 67.',
        },
      ],
    },
    'financial-advisors': {
      pageType: 'financial-advisors',
      h1Template: 'Financial Adviser in {location}',
      introTemplate: 'There\'s a crucial distinction between financial guidance (general information about what you could do) and financial advice (a personalised recommendation to take a specific action). In {location}, both are available, but they come at very different price points. Here\'s what you need to know before you decide.',
      heroSubtext: 'Not sure if you need advice or just guidance? Let\'s clear that up',
      ctaText: 'Book in a free 15 minute call',
      ctaLink: '/book?ref=location-financial-advisors',
      ctaKind: 'booking',
      metaDescription: 'Looking for a financial adviser in {location}? Understand what advice costs, when guidance is enough, and how to work out where you stand first.',
      faqs: [
        {
          question: 'How much does a financial adviser cost in the UK?',
          answer: 'Typically £500-2,000 for a one-off piece of advice, or 0.5-1% of your assets per year for an ongoing relationship. On a £400,000 pension pot, ongoing advice at 1% is £4,000 every year. Always ask for the fee structure in writing before committing; advisers are required to disclose it.',
        },
        {
          question: 'Is it worth paying for a financial adviser?',
          answer: 'For complex, high-stakes decisions (defined benefit pension transfers, large inheritances, intricate tax situations), usually yes. For understanding where you stand and what your options are, often not: free guidance and low-cost coaching cover that ground. The expensive mistake is paying ongoing advice fees for education you could get for a fraction of the price.',
        },
        {
          question: 'How do I find a financial adviser in {location}?',
          answer: 'Check any adviser is FCA-authorised on the Financial Services Register before handing over money. Directories like Unbiased and VouchedFor list advisers serving {location}, though most advisers now work perfectly well over video call, so you\'re not limited to local firms. Prefer independent (whole-of-market) advisers over restricted ones where possible.',
        },
        {
          question: 'What is the difference between an independent and a restricted adviser?',
          answer: 'An independent financial adviser (IFA) can recommend products from across the whole market. A restricted adviser can only recommend from a limited range, often their own company\'s products. Both are FCA-regulated, but independence matters: a restricted adviser can\'t tell you if a better product exists outside their range.',
        },
      ],
    },
    'wealth-managers': {
      pageType: 'wealth-managers',
      h1Template: 'Wealth Managers in {location}',
      introTemplate: 'Wealth management in {location} centres on the reality that average house prices are {housePrice}. Here\'s how to evaluate whether you need professional wealth management.',
      heroSubtext: 'Wealth management comes at a cost. Find out if you really need it',
      ctaText: 'Book in a free 15 minute call',
      ctaLink: '/book?ref=location-wealth-managers',
      ctaKind: 'booking',
      metaDescription: 'Wealth managers in {location} usually serve clients with £250k or more. Find out whether you actually need one, what they charge, and the alternatives.',
      faqs: [
        {
          question: 'How much money do you need for a wealth manager?',
          answer: 'Most UK wealth managers set minimums of £250,000 in investable assets, and many private banks start at £1 million or more. Below that, you\'re unlikely to get their attention, and honestly unlikely to need it: a financial planner, coach, or good self-service tools cover the same ground at a fraction of the cost.',
        },
        {
          question: 'What do wealth managers charge?',
          answer: 'Typically 0.5-1.5% of assets under management per year, sometimes with performance fees on top, plus the underlying fund charges. On £500,000 that\'s £2,500-7,500 annually, every year, regardless of performance. Over a 25-year retirement, those fees compound into a six-figure cost.',
        },
        {
          question: 'Is a wealth manager worth it?',
          answer: 'If you have complex needs (multiple income streams, significant tax planning, business assets, estates), the bundled service can justify the fee. If your situation is a pension, some ISAs, and a property, you\'re mostly paying premium prices for investment management you can get from a low-cost index portfolio.',
        },
        {
          question: 'What is the difference between a wealth manager and a financial adviser?',
          answer: 'A financial adviser gives regulated recommendations, usually on specific decisions. A wealth manager bundles ongoing investment management, tax and estate planning, and a dedicated relationship manager into one premium service for larger portfolios. Same regulation, broader (and pricier) scope.',
        },
      ],
    },
    'financial-planners': {
      pageType: 'financial-planners',
      h1Template: 'Financial Planners in {location}',
      introTemplate: '{location} residents navigating retirement face specific challenges. With average house prices at {housePrice} and living costs of {livingCosts}, getting ahead is essential.',
      heroSubtext: 'Get clear on your numbers and what to do next',
      ctaText: 'Book in a free 15 minute call',
      ctaLink: '/book?ref=location-financial-planners',
      ctaKind: 'booking',
      metaDescription: 'Compare financial planners in {location}: what they do, what they cost, and how to get clear on your retirement numbers before you pay for advice.',
      faqs: [
        {
          question: 'What does a financial planner do?',
          answer: 'A financial planner builds your complete financial picture: what you have, what you need, and the route between the two. That means cash flow modelling, retirement projections, and a written plan. It\'s broader than product advice; the plan comes first, and any products are just tools to execute it.',
        },
        {
          question: 'What is the difference between a financial planner and a financial adviser?',
          answer: 'In the UK the titles overlap and neither is protected, but the emphasis differs: planners focus on the overall plan and cash flow modelling; advisers focus on regulated recommendations for specific products and decisions. Many professionals do both. What matters more is whether they\'re FCA-authorised and how they charge.',
        },
        {
          question: 'How much does a financial plan cost in the UK?',
          answer: 'A full financial plan from a regulated firm typically costs £1,000-3,000 as a fixed fee, or is bundled into ongoing percentage-of-assets charges. The alternative: build the picture yourself with planning tools, or work with a financial coach for a fraction of the cost, and pay for regulated advice only when a specific decision needs it.',
        },
        {
          question: 'Can I do my own financial planning?',
          answer: 'For most situations, yes. Working out your retirement number, mapping your pensions, and modelling scenarios are all things good tools now do well; that\'s exactly what Delphina provides for {location} residents. Where DIY stops making sense is regulated territory: transferring final salary pensions or complex tax structuring.',
        },
      ],
    },
    'financial-coaching': {
      pageType: 'financial-coaching',
      h1Template: 'Financial Coaches in {location}',
      introTemplate: 'Financial coaching in {location} differs from formal financial advice. With annual living costs of {livingCosts}, many retirees find coaching more accessible and action-oriented.',
      heroSubtext: 'Affordable guidance to help you make the right moves with your money',
      ctaText: 'Book in a free 15 minute call',
      ctaLink: '/book?ref=location-financial-coaching',
      ctaKind: 'booking',
      metaDescription: 'Looking for a financial coach in {location}? Get 1-1 financial coaching by video from £99, starting with a free 15-min call. See if you\'re on track.',
      metaTitle: 'Financial Coaches in {location}: 1-1 Coaching by Video, from £99',
      faqs: [
        {
          question: 'What does a financial coach do?',
          answer: 'A financial coach helps you understand your situation, set goals, build a plan you believe in, and stay accountable to it. Sessions are practical: your numbers, your options, your next steps. Unlike a regulated adviser, a coach doesn\'t recommend specific products; they build your ability to decide well yourself.',
        },
        {
          question: 'How much does a financial coach cost in the UK?',
          answer: 'Delphina\'s 1-1 financial coaching starts at £99 per session, with no percentage-of-assets fees. Compare that with £150+ per hour for regulated advice or 0.5-1% of your assets every year for an ongoing adviser relationship. Delphina starts with a free 15 minute call, so you can judge the fit before spending anything.',
        },
        {
          question: 'Do I need a financial coach or a financial adviser?',
          answer: 'Ask what\'s actually blocking you. If it\'s clarity, habits, or confidence ("am I on track?", "what should I prioritise?"), a coach solves that affordably. If it\'s a specific regulated decision (which pension to transfer, which product to buy), you need an FCA-authorised adviser. Many people use a coach first, and advice only at the decision point.',
        },
        {
          question: 'Is financial coaching regulated?',
          answer: 'No. Financial coaching sits outside FCA regulation because coaches don\'t make personal recommendations about specific financial products. That\'s a feature and a limitation: it keeps coaching affordable and education-focused, but it means a coach should always tell you when a decision crosses into regulated territory, and Delphina\'s coaches do.',
        },
        {
          question: 'Can I get a financial coach in {location} without meeting in person?',
          answer: 'Yes. Delphina\'s 1-1 coaching is delivered over video call, so you don\'t need a coach based in {location}. Your real numbers stay on screen in front of both of you, and each session builds on the last. Sessions start at £99, with a free 15 minute call first.',
        },
      ],
    },
    'retirement-planning': {
      pageType: 'retirement-planning',
      h1Template: 'Retirement Planning in {location}',
      introTemplate: 'Retiring in {location} requires understanding local costs. With average house prices of {housePrice} and annual living costs of {livingCosts}, understanding the numbers is essential.',
      heroSubtext: 'It\'s not too late to get started',
      ctaText: 'See if you\'re on track',
      ctaLink: '/login?ref=location-primary',
      ctaKind: 'signup',
      metaDescription: 'Plan your retirement in {location} with local house prices and living costs built in. Work out your number and see if you\'re on track in minutes.',
      faqs: [
        {
          question: 'How do I start planning for retirement?',
          answer: 'Four steps, in order: work out your retirement number (what your lifestyle in {location} will cost, minus the State Pension, times 25); find all your pensions, including old workplace ones; measure the gap between where you are and where you need to be; then decide what to change: contributions, investments, or timeline. The calculator on this page handles step one.',
        },
        {
          question: 'When should I start retirement planning?',
          answer: 'Earlier is exponentially better because investment growth compounds: money invested in your 30s can be worth several times more at retirement than the same amount invested in your 50s. But the second-best time is now. Even five years of focused planning before retirement materially changes your options.',
        },
        {
          question: 'What pensions might I have in the UK?',
          answer: 'Usually three kinds: the State Pension (£12,548 per year from age 67, 2026/27, if you have 35 qualifying years of National Insurance); workplace pensions from every job since auto-enrolment began in 2012, which are easy to lose track of; and any personal pensions or SIPPs you\'ve opened yourself. Most people\'s first win is simply finding them all.',
        },
        {
          question: 'How much should I pay into my pension?',
          answer: 'A common rule of thumb: halve your age when you start, and contribute that percentage of salary (including employer contributions). Started at 30? Aim for 15%. At minimum, always contribute enough to get your full employer match; it\'s an instant 100% return. Beyond rules of thumb, work backwards from your actual retirement number.',
        },
      ],
    },
    'financial-guidance': {
      pageType: 'financial-guidance',
      h1Template: 'Financial Guidance in {location}',
      introTemplate: 'Financial guidance gives you the information to make your own money decisions, without paying for regulated financial advice. For {location} residents facing living costs of {livingCosts} a year, guidance is often the most accessible place to start.',
      heroSubtext: 'Understand your options without paying for regulated advice',
      ctaText: 'Book in a free 15 minute call',
      ctaLink: '/book?ref=location-financial-guidance',
      ctaKind: 'booking',
      metaDescription: 'Get financial guidance in {location}: 1-1 coaching by video from £99, with a free 15-min call to start. Understand your pensions and see if you\'re on track.',
      metaTitle: 'Financial Guidance in {location}: 1-1 Coaching by Video, from £99',
      faqs: [
        {
          question: 'What is the difference between financial guidance and financial advice?',
          answer: 'Financial guidance gives you general information about your options: what you could do and what to consider, so you can make your own decision. Financial advice is a personal recommendation from an FCA-regulated adviser telling you what to do based on your individual circumstances. Guidance is unregulated and often free; advice is regulated and paid for.',
        },
        {
          question: 'Is financial guidance free in the UK?',
          answer: 'Much of it is. Government-backed services like MoneyHelper and Pension Wise offer free guidance, and Delphina\'s self-service tools are free to get started. Paid guidance, like 1-1 financial coaching, starts at £99 a session - a small fraction of regulated advice fees.',
        },
        {
          question: 'Is financial guidance enough, or do I need regulated advice?',
          answer: 'For most straightforward situations (understanding your pensions, working out your retirement number, deciding how much to save), guidance is enough. If you\'re making a high-stakes, irreversible decision, like transferring a defined benefit pension or handling a complex inheritance, that\'s when paying for regulated advice is usually worth it.',
        },
        {
          question: 'Where can I get financial guidance in {location}?',
          answer: 'You don\'t need to find someone local. Free national services like MoneyHelper cover {location}, and online services like Delphina give you personalised guidance tools plus optional 1-1 coaching over video call from £99, with a free 15 minute call to start - wherever you live in the UK.',
        },
      ],
    },
    'financial-mentoring': {
      pageType: 'financial-mentoring',
      h1Template: 'Financial Mentors in {location}',
      introTemplate: 'A financial mentor helps you build money confidence over time, someone who\'s been there before and helps you avoid the expensive mistakes. In {location}, where the average retirement number is well into six figures, that kind of ongoing support can be the difference between drifting and making progress.',
      heroSubtext: 'Ongoing support from people who\'ve done it before',
      ctaText: 'Book in a free 15 minute call',
      ctaLink: '/book?ref=location-financial-mentoring',
      ctaKind: 'booking',
      metaDescription: 'Looking for a financial mentor in {location}? Get 1-1 financial mentoring by video from £99, starting with a free 15-min call. See if you\'re on track.',
      metaTitle: 'Financial Mentors in {location}: 1-1 Mentoring by Video, from £99',
      faqs: [
        {
          question: 'What does a financial mentor do?',
          answer: 'A financial mentor shares experience and perspective over an ongoing relationship: helping you build good money habits, think through decisions, and stay accountable to your goals. Unlike a regulated adviser, a mentor doesn\'t tell you which products to buy; they help you become better at managing money yourself.',
        },
        {
          question: 'What is the difference between financial mentoring and financial coaching?',
          answer: 'They overlap a lot. Mentoring is typically experience-led and relationship-based: guidance from someone further down the road than you. Coaching is more structured: sessions focused on specific goals, with a clear plan and accountability. Delphina\'s coaches blend both, structured sessions backed by real experience.',
        },
        {
          question: 'How much does a financial mentor cost in the UK?',
          answer: 'Informal mentoring is sometimes free, but structured mentoring or coaching at Delphina starts at £99 per session, compared to £150+ per hour for regulated financial advice. Every relationship starts with a free 15 minute call so you can see if it\'s a fit before paying anything.',
        },
        {
          question: 'Can I get a financial mentor in {location} without meeting in person?',
          answer: 'Yes. Delphina\'s 1-1 mentoring is delivered over video call, so you get an ongoing mentoring relationship wherever you live in the UK. Your real numbers stay on screen in front of both of you, sessions build on the last, and you don\'t pay local in-person rates. Mentoring starts at £99 a session, with a free 15 minute call first.',
        },
      ],
    },
    'financial-therapists': {
      pageType: 'financial-therapists',
      h1Template: 'Financial Therapists in {location}',
      introTemplate: 'Financial therapy deals with the emotional side of money: anxiety, avoidance, overspending, and conflict in relationships. If money stress is affecting your wellbeing in {location}, it helps to understand what financial therapy is, when you need it, and when clarity about your numbers is what actually lifts the weight.',
      heroSubtext: 'Money stress is real. Start by understanding where you stand',
      ctaText: 'Book in a free 15 minute call',
      ctaLink: '/book?ref=location-financial-therapists',
      ctaKind: 'booking',
      metaDescription: 'Considering a financial therapist in {location}? Learn what financial therapy treats, what it costs, and when financial clarity or coaching is the better first step.',
      faqs: [
        {
          question: 'What is a financial therapist?',
          answer: 'A financial therapist combines counselling skills with financial knowledge to help with the emotional and psychological side of money: financial anxiety, compulsive spending, money avoidance, financial trauma, and money conflict in relationships. Most have a background in counselling or psychotherapy rather than financial planning.',
        },
        {
          question: 'How much does financial therapy cost in the UK?',
          answer: 'Financial therapy is typically priced like private counselling: around £50-120 per session in the UK, depending on the practitioner\'s qualifications and experience. It\'s rarely covered by the NHS, though talking therapies for general anxiety are available through NHS referral.',
        },
        {
          question: 'Do I need a financial therapist or a financial coach?',
          answer: 'If money problems are rooted in emotions, past experiences, or relationship conflict, and better information alone doesn\'t change your behaviour, a financial therapist is the right specialist. If your stress mainly comes from uncertainty (not knowing where you stand or whether you\'ll be okay), a financial coach and clear numbers often resolve it faster. The two aren\'t mutually exclusive.',
        },
        {
          question: 'Can financial therapy help with money anxiety?',
          answer: 'Yes, that\'s one of its main uses. But it\'s worth noting that a large part of money anxiety is uncertainty. Many people find that simply seeing their full financial picture, their retirement number, and a realistic plan, significantly reduces the anxiety on its own. That\'s the clarity Delphina is built to provide.',
        },
      ],
    },
  };
  return configs[pageType];
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getLocationComparison(location: LocationData): string {
  const diff = location.annualLivingCosts - ukAverageLivingCosts;
  const pct = Math.abs(Math.round((diff / ukAverageLivingCosts) * 100));

  if (diff < 0) {
    return `${pct}% below average`;
  } else if (diff > 0) {
    return `${pct}% above average`;
  }
  return 'in line with average';
}
